import {
  FetchAdapter,
  NAuthClient,
  NAuthClientError,
  defaultEndpoints,
  type HttpAdapter,
  type HttpRequest,
  type HttpResponse,
} from '@nauth-toolkit/client';
import { API_BASE_URL } from './env';

const AUTH_PATH_PREFIX = '/auth';

/** A 401 from these is an answer about the credentials, not an expired access token. */
const PRE_AUTH_PATHS = [
  defaultEndpoints.login,
  defaultEndpoints.refresh,
  defaultEndpoints.socialExchange,
  defaultEndpoints.forgotPassword,
  defaultEndpoints.confirmForgotPassword,
  defaultEndpoints.respondChallenge,
].map((path) => `${AUTH_PATH_PREFIX}${path}`);

function isPreAuthRequest(url: string): boolean {
  const { pathname } = new URL(url);
  return PRE_AUTH_PATHS.some((path) => pathname.endsWith(path));
}

function hasBearer(headers: Record<string, string> | undefined): boolean {
  return Object.keys(headers ?? {}).some((name) => name.toLowerCase() === 'authorization');
}

function withBearer(
  headers: Record<string, string> | undefined,
  accessToken: string,
): Record<string, string> {
  const next: Record<string, string> = {};
  for (const [name, value] of Object.entries(headers ?? {})) {
    if (name.toLowerCase() !== 'authorization') next[name] = value;
  }
  next['Authorization'] = `Bearer ${accessToken}`;
  return next;
}

/**
 * nauth's FetchAdapter makes one attempt and throws on a 401; refresh-and-retry
 * is left to the consumer (see nauth's React setup guide). Two differences from
 * the guide's cookie-mode example, both required under json delivery:
 *
 * - the retry swaps in the NEW bearer — replaying the original headers would
 *   resend the expired token and 401 again;
 * - a failed refresh rethrows the original 401, so callers keep seeing
 *   "unauthenticated" rather than the refresh's own error.
 *
 * Concurrent 401s share one refresh: `refreshTokens()` is single-flight inside
 * the SDK's token manager.
 */
class RefreshingFetchAdapter implements HttpAdapter {
  private readonly inner = new FetchAdapter();

  async request<T>(config: HttpRequest): Promise<HttpResponse<T>> {
    try {
      return await this.inner.request<T>(config);
    } catch (error) {
      const retryable =
        error instanceof NAuthClientError &&
        error.statusCode === 401 &&
        hasBearer(config.headers) &&
        !isPreAuthRequest(config.url);
      if (!retryable) throw error;

      const accessToken = await refreshAccessToken();
      if (accessToken === null) throw error;

      return this.inner.request<T>({ ...config, headers: withBearer(config.headers, accessToken) });
    }
  }
}

export const authHttpAdapter = new RefreshingFetchAdapter();

export const authClient = new NAuthClient({
  baseUrl: API_BASE_URL,
  authPathPrefix: AUTH_PATH_PREFIX,
  tokenDelivery: 'json',
  httpAdapter: authHttpAdapter,
  // React Router owns navigation. Without this the SDK's challenge router calls
  // window.location.replace() after every sign-in and reloads the app.
  onAuthResponse: () => {},
  debug: import.meta.env.DEV,
});

/** Resolves to the new access token, or null when the session is gone. */
export async function refreshAccessToken(): Promise<string | null> {
  try {
    return (await authClient.refreshTokens()).accessToken;
  } catch {
    return null;
  }
}

export async function authorizationHeader(): Promise<Record<string, string>> {
  const accessToken = await authClient.getAccessToken();
  return accessToken === null ? {} : { Authorization: `Bearer ${accessToken}` };
}
